import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { WrappedFieldArrayProps } from 'redux-form';

import type { PreviewVM } from 'ab-viewModels/account/preview.viewModel';

import type NotifyCompanyRequestModel from 'ab-requestModels/companyNotify.requestModel';

import { NotificationPriority } from '@acceligentllc/shared/enums/notificationPriority';

import Dropdown from 'af-components/Controls/Dropdown';

import * as AccountActions from 'af-actions/accounts';

import AccountPreview from './AccountPreview';

interface OwnProps extends WrappedFieldArrayProps<PreviewVM> {
	fixed: boolean;
	notificationPriority: NotificationPriority;
	selectedUserGroups?: NotifyCompanyRequestModel['userGroups'];
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const Accounts: React.FC<Props> = (props: Props) => {

	const { fields, notificationPriority, selectedUserGroups, fixed, findAllAccountsForCompany } = props;

	const [accounts, setAccounts] = React.useState<PreviewVM[]>([]);

	const loadAccountsForCompany = React.useCallback(async () => {
		const _accounts = await findAllAccountsForCompany();
		setAccounts(_accounts);
	}, [findAllAccountsForCompany]);

	React.useEffect(() => {
		loadAccountsForCompany();
	}, [loadAccountsForCompany]);

	const filterOptions = React.useCallback((
		allOptions: PreviewVM[],
		selectedGroups: NotifyCompanyRequestModel['userGroups'] = [],
		selectedAccounts: PreviewVM[] = []
	) => {

		const reduceAccounts = (_acc: Record<number, true>, _account: PreviewVM) => {
			_acc[_account.id] = true;
			return _acc;
		};
		const _accounts = selectedAccounts.reduce(reduceAccounts, {});

		selectedGroups.forEach((_userGroup) => _userGroup.accounts?.reduce(reduceAccounts, _accounts));

		return allOptions.filter((_account) => !_accounts[_account.id]);
	}, []);

	const options = React.useMemo(() => {
		return filterOptions(accounts, selectedUserGroups, fields.getAll());
	}, [accounts, fields, filterOptions, selectedUserGroups]);

	const renderOption = React.useCallback((option: PreviewVM) => {
		return (<span>{option.fullName} ({option.uniqueId})</span>);
	}, []);

	const filterOption = React.useCallback((option: PreviewVM, rawInput: string) => {
		const { fullName, uniqueId } = option;

		return `${fullName} ${uniqueId}`.toLowerCase().includes(rawInput.toLowerCase());
	}, []);

	const removeAccount = React.useCallback((index: number) => {
		fields.remove(index);
	}, [fields]);

	const onChange = React.useCallback(async (option: PreviewVM) => {
		fields.push(option);
	}, [fields]);

	return (
		<>
			<div>
				<Dropdown<PreviewVM>
					filterable={true}
					filterBy={filterOption}
					fixed={fixed}
					forcePlaceholder={true}
					isWhite={true}
					label="Additional Employees"
					onValueChange={onChange}
					options={options}
					placeholder="Select employees"
					renderMenuItem={renderOption}
					valueKey="id"
					withBorder={true}
					withCaret={true}
				/>
			</div>
			<div className="pills">
				{!!fields.length && fields.map((_accountFieldName, _index) => {
					const _account = fields.get(_index);
					const hasEmail = !!_account.email;
					const hasPhoneNumber = !!_account.phoneNumber;

					const willReceiveNotification =
						(notificationPriority === NotificationPriority.EMAIL_ONLY && hasEmail)
						|| (notificationPriority === NotificationPriority.SMS_ONLY && hasPhoneNumber)
						|| [NotificationPriority.EMAIL_FIRST, NotificationPriority.SMS_FIRST, NotificationPriority.SEND_BOTH].includes(notificationPriority)
						&& (hasEmail || hasPhoneNumber);

					return (
						<AccountPreview
							account={_account}
							hasEmail={hasEmail}
							index={_index}
							key={`account#${_index}`}
							removeAccount={removeAccount}
							willReceiveNotification={willReceiveNotification}
						/>
					);
				}
				)}
				{!fields.length &&
					<div>No additional employees selected</div>
				}
			</div>
		</>
	);
};

function mapDispatchToProps() {
	return {
		findAllAccountsForCompany: AccountActions.findAllForCompanyPreview,
	};
}

const connector = connect(null, mapDispatchToProps());

export default connector(React.memo(Accounts));
